import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Plus, Trash2, X, FolderPlus } from "lucide-react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function Items({ wl, tabId, onRemove, navigate }) {
  const rows = wl.items.filter((i) => (i.tab_id || null) === tabId).sort((a, b) => (b.added_at || "").localeCompare(a.added_at || ""));
  if (!rows.length) return <p className="text-xs font-mono text-slate-500 py-6 text-center" data-testid={`wl-empty-${wl.id}-${tabId || "main"}`}>No stocks yet — use the + button next to any stock.</p>;
  return (
    <table className="w-full text-xs font-mono" data-testid={`wl-table-${wl.id}-${tabId || "main"}`}>
      <thead><tr className="text-[10px] text-slate-500 border-b border-slate-200">
        <th className="text-left py-1.5 px-2">Symbol</th><th className="text-left px-2">Added on</th>
        <th className="text-right px-2">Price then</th><th className="text-right px-2">Price now</th><th className="text-right px-2">Since added</th><th />
      </tr></thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.instrument_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/instrument/${i.instrument_id}`)}
            data-testid={`wl-row-${i.instrument.symbol}`}>
            <td className="py-1.5 px-2 font-bold text-slate-900">{i.instrument.display_symbol || i.instrument.symbol}</td>
            <td className="px-2 text-slate-600" data-testid={`wl-added-${i.instrument.symbol}`}>{fmtDate(i.added_at)}</td>
            <td className="px-2 text-right text-slate-600">{i.added_price != null ? `₹${inr(i.added_price)}` : "—"}</td>
            <td className="px-2 text-right text-slate-900">{i.ltp != null ? `₹${inr(i.ltp)}` : "—"}</td>
            <td className={`px-2 text-right font-bold ${i.since_pct == null ? "text-slate-500" : i.since_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`wl-since-${i.instrument.symbol}`}>
              {i.since_pct == null ? "—" : `${i.since_pct > 0 ? "+" : ""}${i.since_pct}%`}</td>
            <td className="px-2 text-right">
              <button onClick={(e) => { e.stopPropagation(); onRemove(i); }} data-testid={`wl-remove-${i.instrument.symbol}`} className="text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WatchlistCard({ wl, reload, navigate }) {
  const [tab, setTab] = useState(null);
  const [newTab, setNewTab] = useState("");
  const safe = (fn) => async (...a) => { try { await fn(...a); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); reload(); } };
  const addTab = safe(async () => {
    if (!newTab.trim()) return;
    const r = await api.post(`/watchlists/${wl.id}/tabs`, { name: newTab });
    setNewTab(""); setTab(r.data.id); toast.success(`Tab "${r.data.name}" added`); reload();
  });
  const delTab = safe(async (t) => { await api.delete(`/watchlists/${wl.id}/tabs/${t.id}`); setTab(null); reload(); });
  const remove = safe(async (i) => { await api.delete(`/watchlists/${wl.id}/items/${i.instrument_id}`, { params: { tab_id: i.tab_id || undefined } }); reload(); });
  const count = (id) => wl.items.filter((i) => (i.tab_id || null) === id).length;
  return (
    <div className="border border-slate-200 bg-white" data-testid={`wl-${wl.id}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Bookmark className="h-4 w-4 text-blue-600" />{wl.name}
          <span className="text-[10px] font-mono text-slate-500 font-normal">created {fmtDate(wl.created_at)}</span></h3>
        {wl.name !== "My Watchlist" && (
          <button onClick={async () => { await api.delete(`/watchlists/${wl.id}`); reload(); }} data-testid={`wl-delete-${wl.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1 px-3 pt-2">
        <button onClick={() => setTab(null)} data-testid={`wl-tab-main-${wl.id}`}
          className={`h-7 px-2.5 text-[11px] font-mono border ${tab === null ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600"}`}>Main · {count(null)}</button>
        {(wl.tabs || []).map((t) => (
          <span key={t.id} className={`h-7 inline-flex items-center border ${tab === t.id ? "bg-blue-600 border-blue-600" : "border-slate-200"}`}>
            <button onClick={() => setTab(t.id)} data-testid={`wl-tab-${t.id}`} title={`added ${fmtDate(t.created_at)}`}
              className={`px-2.5 text-[11px] font-mono ${tab === t.id ? "text-white" : "text-slate-600"}`}>{t.name} · {count(t.id)}
              <span className={`ml-1.5 text-[9px] ${tab === t.id ? "text-white/80" : "text-slate-400"}`}>{fmtDate(t.created_at).split(",")[0]}</span></button>
            <button onClick={() => delTab(t)} data-testid={`wl-tab-delete-${t.id}`} className={`pr-1.5 hover:text-rose-400 ${tab === t.id ? "text-white" : "text-slate-400"}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        <span className="inline-flex items-center gap-1 ml-1">
          <Input value={newTab} onChange={(e) => setNewTab(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTab()} placeholder="New tab name"
            className="h-7 w-36 text-[11px] font-mono" data-testid={`wl-newtab-input-${wl.id}`} />
          <button onClick={addTab} data-testid={`wl-newtab-add-${wl.id}`} className="h-7 px-2 border border-slate-300 text-slate-600 hover:text-blue-600 hover:border-blue-500" title="Add tab">
            <FolderPlus className="h-3.5 w-3.5" /></button>
        </span>
      </div>
      <div className="p-2"><Items wl={wl} tabId={tab} onRemove={remove} navigate={navigate} /></div>
    </div>
  );
}

export default function Watchlists() {
  const [lists, setLists] = useState(null);
  const [name, setName] = useState("");
  const navigate = useNavigate();
  const load = () => api.get("/watchlists").then((r) => setLists(r.data.watchlists)).catch(() => setLists([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return;
    await api.post("/watchlists", { name }); setName(""); toast.success("Watchlist created"); load();
  };
  return (
    <div className="space-y-4" data-testid="watchlists-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Watchlists</h1>
          <p className="text-xs font-mono text-slate-500 mt-0.5">tap + next to any stock (or tap the stock → Add to watchlist) · each entry keeps its date and price when added</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="New watchlist" className="h-9 w-48" data-testid="wl-new-input" />
          <Button onClick={create} data-testid="wl-new-btn" className="h-9"><Plus className="h-4 w-4 mr-1" />Create</Button>
        </div>
      </div>
      {!lists ? <p className="text-xs font-mono text-slate-500">Loading…</p> : lists.map((wl) => <WatchlistCard key={wl.id} wl={wl} reload={load} navigate={navigate} />)}
    </div>
  );
}
